import type { ReactNode } from "react";

export interface ActionButtonProps {
  label: string;
  icon: ReactNode;
  variant: "primary" | "secondary" | "danger";
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}

/**
 * ActionButton - A styled button for admin actions.
 *
 * Variants:
 * - primary: Purple/magic color for main actions
 * - secondary: Gray for supplementary actions
 * - danger: Red for destructive actions
 */
export function ActionButton({
  label,
  icon,
  variant,
  onClick,
  loading = false,
  disabled = false,
}: ActionButtonProps) {
  const baseStyles = `
    inline-flex items-center gap-2 px-4 py-2.5
    text-sm font-medium rounded-lg
    transition-all duration-150
    focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  const variantStyles = {
    primary: `
      bg-magic-600 text-white
      hover:bg-magic-700
      focus-visible:ring-magic-500
    `,
    secondary: `
      bg-gray-100 text-gray-700
      hover:bg-gray-200
      focus-visible:ring-gray-400
    `,
    danger: `
      bg-red-600 text-white
      hover:bg-red-700
      focus-visible:ring-red-500
    `,
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseStyles} ${variantStyles[variant]}`}
    >
      {loading ? (
        <LoadingSpinner className="w-4 h-4" />
      ) : (
        <span className="w-4 h-4 flex-shrink-0">{icon}</span>
      )}
      <span>{label}</span>
    </button>
  );
}

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

export default ActionButton;
